import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { observer } from 'mobx-react-lite';

import { getTopWinsDateRange, formatDate, dotFormatter } from '../../../../../../helpers';
import { TopWinsDateRange, TopWinsOrderType } from '../../../../../../enums';
import { TopWinsTabs, TopWinsTabKey } from './top-wins-tabs';
import { GamesTabs, GamesTabKey } from './games-tabs';
import { useStore } from '../../../../../../hooks';
import { TopWin } from '../../../../../../models';
import { TopWinsEmpty } from './top-wins-empty';
import { WinnersSkeleton } from './skeleton';
import { WinnersCard } from './winners-card';

const TOP_WINS_TAKE = 50;

// How the Top Winners events name each filter value
const PLAYERS_FILTER: Record<TopWinsTabKey, string> = {
  [TopWinsTabKey.MyTopWins]: 'my',
  [TopWinsTabKey.AllTopWins]: 'all',
};
const GAMES_FILTER: Record<GamesTabKey, string> = {
  [GamesTabKey.CurrentGame]: 'current',
  [GamesTabKey.AllGames]: 'all',
};
const ORDER_FILTER: Record<TopWinsOrderType, string> = {
  [TopWinsOrderType.Odd]: 'odd',
  [TopWinsOrderType.WinAmount]: 'win',
  [TopWinsOrderType.BetAmount]: 'bet',
};
const RANGE_FILTER: Record<TopWinsDateRange, string> = {
  [TopWinsDateRange.Today]: 'today',
  [TopWinsDateRange.ThisWeek]: 'week',
  [TopWinsDateRange.ThisMonth]: 'month',
  [TopWinsDateRange.AllTime]: 'all',
};

export const SidebarWinnersSection: React.FC = observer(() => {
  const { gameStore, sidebarMenuStore, userActivityStore } = useStore();
  const { topWins: { isLoading, data: wins }, gameId, precision, hasGameRedirection } = gameStore;
  const { isSidebarOpen } = sidebarMenuStore;

  const [activeGamesTab, setActiveGamesTab] = useState<GamesTabKey>(GamesTabKey.CurrentGame);
  // "All players" first: "My wins" for this game this month is empty for most players
  const [activeTopWinsTab, setActiveTopWinsTab] = useState<TopWinsTabKey>(TopWinsTabKey.AllTopWins);
  const [orderType, setOrderType] = useState<TopWinsOrderType>(TopWinsOrderType.Odd);
  const [dateRange, setDateRange] = useState<TopWinsDateRange>(TopWinsDateRange.ThisMonth);

  // The first list after the sidebar opens (or this tab mounts while open) is an "open"; the rest follow a filter change.
  const nextTriggerRef = useRef<'open' | 'filter'>('open');

  // The section stays mounted while the sidebar is closed (it is the default tab when no promo
  // tools are enabled), so only fetch while the sidebar is actually open; reopening refreshes the list.
  useEffect(() => {
    if (!isSidebarOpen) {
      nextTriggerRef.current = 'open';
      return;
    }
    const trigger = nextTriggerRef.current;
    nextTriggerRef.current = 'filter';
    // a list replaced by a newer request, or still loading when the sidebar closed, was never shown
    let current = true;
    const { dateFrom, dateTo } = getTopWinsDateRange(dateRange);
    gameStore.getTopWins({
      dateTo,
      dateFrom,
      orderType,
      take: TOP_WINS_TAKE,
      isUserTop: activeTopWinsTab === TopWinsTabKey.MyTopWins,
      gameId: activeGamesTab === GamesTabKey.CurrentGame ? gameId : undefined,
    }).then((rows) => {
      if (!current || !rows) return;
      userActivityStore.sendTrackUserActivity({
        category: UserActivityCategory.PromoTools,
        type: UserActivityTypes.TopWinnersShown,
        data: {
          trigger,
          players: PLAYERS_FILTER[activeTopWinsTab],
          games: GAMES_FILTER[activeGamesTab],
          order: ORDER_FILTER[orderType],
          range: RANGE_FILTER[dateRange],
          rows: rows.length,
        }
      });
    });
    return () => {
      current = false;
    };
  }, [isSidebarOpen, activeGamesTab, activeTopWinsTab, orderType, dateRange, gameId]);

  const handleFilterApply = useCallback((nextOrderType: TopWinsOrderType, nextDateRange: TopWinsDateRange) => {
    setOrderType(nextOrderType);
    setDateRange(nextDateRange);
  }, []);

  const handleReplayClick = useCallback((win: TopWin) => {
    gameStore.trackTopWinsReplay(win);
    gameStore.openReplay(win);
    sidebarMenuStore.setIsSidebarOpen(false);
  }, []);

  // rank = the card's place in the list, 1 = top
  const handleWinCardClick = useCallback((win: TopWin, rank: number) => {
    // the dialog offers Watch Replay and/or Play Now; with neither available it would be empty
    if (!win.hasReplay && !hasGameRedirection) return;
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.TopWinnersCard,
      data: { gameId: win.gameId, rank, players: PLAYERS_FILTER[activeTopWinsTab] }
    });
    gameStore.setReplayDialogWin(win);
  }, [activeTopWinsTab, hasGameRedirection]);

  return (
    <>
      <div className="side-menu-new-content-holder-top">
        <GamesTabs activeTab={activeGamesTab} onChange={setActiveGamesTab} />
        <TopWinsTabs
          activeTab={activeTopWinsTab}
          onChange={setActiveTopWinsTab}
          orderType={orderType}
          dateRange={dateRange}
          onFilterApply={handleFilterApply}
        />
      </div>
      {
        isLoading ? <WinnersSkeleton /> : (
          <div className="side-menu-new-content">
            <div className="side-menu-new-content-inner">
              {wins.length ? (
                wins.map((win, index) => (
                  <WinnersCard
                    key={win.date}
                    title={win.gameName}
                    playerId={win.playerId}
                    currency={win.currency}
                    thumbnail={win.thumbnail}
                    hasReplay={win.hasReplay}
                    date={formatDate(new Date(win.date))}
                    multiplier={win.odd ? `x${win.odd}` : '-'}
                    onCardClick={() => handleWinCardClick(win, index + 1)}
                    onReplayClick={() => handleReplayClick(win)}
                    amount={dotFormatter(win.winAmount, precision)}
                    showThumbnail={activeGamesTab !== GamesTabKey.CurrentGame}
                  />
                ))
              ) : (
                <TopWinsEmpty
                  activeGamesTab={activeGamesTab}
                  activeTopWinsTab={activeTopWinsTab}
                />
              )}
            </div>
          </div>
        )
      }
    </>
  );
});
